import React, { useContext } from 'react'
import styled from 'styled-components'

import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import Dropdown from '@jarvis.oddle/core/components/Dropdown'
import IconTriangle from '@jarvis.oddle/core/components/Icons/IconTriangle'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Button from '@jarvis.oddle/core/components/Button'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  fetchAvailableDateAction,
  fetchTimeslotAction
} from 'src/redux/reservation'
import moment from 'moment/moment'

const DropdownContainer = styled(Div)`
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 60vh;
  overflow: auto;
  min-width: 280px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  z-index: 10;
  ${(props) => (props.show ? '' : 'display: none;')}
`

const StyledDropdownLabel = styled(Flex).attrs(() => ({
  role: 'button',
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #d6d6d6;
  box-sizing: border-box;
  border-radius: 2px;
  padding: 0 10px;
`

const StyledIcon = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'end'
}))`
  margin-right: 10px;
`

const StyledTextContainer = styled(Flex)`
  align-items: start;
`

const StyledText = styled(H4)`
  margin: 21px 11px 8px;
  padding-top: 3px;
  color: #000;
`

const StyledLabel = styled(P)`
  position: absolute;
  top: 0;
  font-weight: 400;
  font-size: 11px !important;
  line-height: 12px !important;
  color: #bbbbbb;
  padding-left: 10px;
`

const StyledButton = styled(Button)`
  margin-top: 20px;
`

const StyledLabelQuantitySelector = styled(H4)`
  color: #000;
`

const OrderSummaryDropdown = (): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { values, setFieldValue } = useFormContext()
  const dispatch = useAppDispatch()

  const minPartySize = values.outlet?.minPartySize || 1
  const maxPartySize = values.outlet?.maxPartySize || 5

  const onSelectedPax = async (closeDropdown) => {
    closeDropdown()
    if (values?.outlet?.id) {
      await dispatch(
        fetchAvailableDateAction({
          outletId: values.outlet.id,
          pax: values.numberOfChildren + values.numberOfAdult,
          month: moment(new Date(values.date)).format('YYYY-MM')
        })
      )
      const newTimeslot = await dispatch(
        fetchTimeslotAction({
          outletId: values.outlet.id,
          pax: values.numberOfChildren + values.numberOfAdult,
          date: moment(new Date(values.date)).format('YYYY-MM-DD')
        })
      )
      if (newTimeslot?.payload?.[0]) {
        setFieldValue('slot', newTimeslot.payload[0], false)
      }
    }
  }

  return (
    <Dropdown
      renderLabel={({ toggleDropdown }) => (
        <StyledDropdownLabel
          data-testid='paxDropdown.label'
          onClick={toggleDropdown}
        >
          <StyledTextContainer>
            <StyledLabel>{t('Pax')}</StyledLabel>
            <StyledText>
              {values.numberOfAdult}{' '}
              {values.numberOfAdult > 1 ? t('Adults') : t('Adult')}
              {values.numberOfChildren === 0
                ? ''
                : values.numberOfChildren === 1
                ? `, 1 ${t('Child')}`
                : `, ${values.numberOfChildren} ${t('Children')}`}
            </StyledText>
          </StyledTextContainer>
          <StyledIcon>
            <IconTriangle iconSize='medium' fill='#000' />
          </StyledIcon>
        </StyledDropdownLabel>
      )}
      renderDropdown={({ isDropdownOpening, closeDropdown }) =>
        isDropdownOpening ? (
          <>
            <Flex
              flexDirection='row'
              alignItems='flex-start'
              justifyContent='space-between'
            >
              <StyledLabelQuantitySelector
                marginTop={marginSizes.small}
                marginBottom={marginSizes.small}
              >
                {t('Adults')}
              </StyledLabelQuantitySelector>
              <Form.QuantitySelector
                withInput
                name={'numberOfAdult'}
                resetOnUnmount={false}
                min={
                  minPartySize - values.numberOfChildren > 0
                    ? minPartySize - values.numberOfChildren
                    : 0
                }
                max={maxPartySize - values.numberOfChildren}
                disableIncrease={
                  values.numberOfAdult + values.numberOfChildren >= maxPartySize
                }
              />
            </Flex>
            <Flex
              flexDirection='row'
              alignItems='flex-start'
              justifyContent='space-between'
              marginTop={marginSizes.medium}
            >
              <StyledLabelQuantitySelector
                marginTop={marginSizes.small}
                marginBottom={marginSizes.small}
              >
                {t('Children')}
              </StyledLabelQuantitySelector>
              <Form.QuantitySelector
                withInput
                name={'numberOfChildren'}
                resetOnUnmount={false}
                min={
                  minPartySize - values.numberOfAdult > 0
                    ? minPartySize - values.numberOfAdult
                    : 0
                }
                max={maxPartySize - values.numberOfAdult}
                disableIncrease={
                  values.numberOfAdult + values.numberOfChildren >= maxPartySize
                }
              />
            </Flex>
            <StyledButton
              fullWidth
              secondary
              onClick={() => onSelectedPax(closeDropdown)}
            >
              {t('Confirm')}
            </StyledButton>
          </>
        ) : null
      }
      DropdownContainer={DropdownContainer}
    />
  )
}

export default OrderSummaryDropdown
